import React, {Component} from 'react';
import PropTypes from 'prop-types';
import cs from 'classnames';
import Dotdotdot from 'react-dotdotdot';
import {FormattedMessage} from 'react-intl';

import {Button, Panel} from '@stubhub/bamboo';
import CBTRedirection from '@stubhub/react-cbt-redirection';
// eslint-disable-next-line import/no-named-as-default
import DateStamp from '@stubhub/react-date-stamp';
import {getEventTime, getEventDate, getVenueInfo} from '@stubhub/react-event-helper';
import Urgency from '@stubhub/react-urgency';

import {buildEventsJSONLD, SCHEMA_ORG_PREFIX, checkJSONLDAndMicroDataEnabled} from '../../helpers/jsonld-helper';
import {GROUPING_IDS} from '../../utils/constants';
import EventMicroData from '../react-event-micro-data';

import {PriceInfo} from './priceInfo';

import './index.scss';

export const isExternalWithoutTickets = (totalTickets = 0, url) => {
  if (__CLIENT__ && window.location && window.location.origin && window.location.hostname) {
    const fullURL = new URL(url, window.location.origin);

    return fullURL.hostname !== window.location.hostname && totalTickets < 1; // Apparently some events can have negative total tickets, so checking < 1 instead of === 0
  }

  return true;
};

class EventItem extends Component {
  static contextTypes = {
    globalRegistry: PropTypes.object,
  };

  navigateTo = (e) => {
    e && e.stopPropagation();
    e && e.preventDefault();
    const links = this.mainNode.getElementsByClassName('EventItem__TitleLink');
    if (links && links[0]) {
      links[0].click();
    }
  };

  /**
   * React Lifecycle Methods
   *
   */
  render() {
    const {
      event,
      type = 'default',
      eventItemDisplayOptions = {},
      showDistance,
      onClick,
      entityType,
      isRedirectModalEnabled,
    } = this.props;

    /**
     * Event Item Display Options to Control the Visibility and Styling of the Event item.
     *
     */
    const {
      disableDateStamp,
      disableVenueAndTime,
      disablePrice,
      className: eventItemClassName,
      hideBorder,
    } = eventItemDisplayOptions;
    const {globalRegistry} = this.context;
    const {name, ticketInfo = {}, venue = {}, groupings = [], sourceId, eventDateLocal} = event;

    /**
     * Event Date and Time Section
     *
     */
    const eventDate = getEventDate(event);
    const timeDisplay = getEventTime(event);

    let {distance = null} = event;
    distance = distance !== null ? Math.ceil(distance) : distance;
    const distanceVal = distance !== null ? ` (${distance} mi)` : '';
    const venueDistance =
      distance !== null && showDistance ? <span className="EventItem__VenueInfo-dist">{distanceVal}</span> : null;

    /**
     * Event Venue Section
     *
     */
    let infoDisplay = null;
    if (!disableVenueAndTime) {
      const {venueLocation, venueName, venueCountry} = getVenueInfo(event);
      const venueDisplay = (
        <span className="EventItem__VenueInfo" data-country={venueCountry}>
          {venueName}, {venueLocation}
          {venueDistance}
        </span>
      );
      infoDisplay = (
        <div className="EventItem__MixInfo">
          <span>
            <time>{timeDisplay}</time> - {venueDisplay}
          </span>
        </div>
      );
    }

    /**
     * Event Price Section
     * TODO - it may depends on other things for show tickets left info (some countries only)
     *
     */
    const {totalTickets, currencyCode: ticketCurrencyCode, minPrice, minListPrice} = ticketInfo;
    const isPriceIncludeFees = globalRegistry.getFeatureValue('common.priceIncludeFees');
    const hideTicketBisPrice = globalRegistry.getFeatureValue('browse.hideTicketBisPrice') && sourceId === '4001';

    /*
     * For Super Bowl events, See Prices text to be shown instead of price
     * Super Bowl events are assigned to grouping id 542
     */
    const showSeePricesForSuperBowlEvent =
      groupings.findIndex((grouping) => grouping.id === GROUPING_IDS.SUPERBOWL) !== -1;

    /**
     * Showing see prices for CA events accessed from .ca tld
     */
    const webTLD = globalRegistry.getDefaultWebTLD();
    const showSeePricesForCA = webTLD === 'ca' && venue.country === 'CA';

    /**
     * Price shown in desktop/tablet
     * Price shown in mobile only if toggle is enabled
     *
     */
    const showPriceInfo = !disablePrice && type === 'default' && totalTickets > 0;
    const price = isPriceIncludeFees ? minPrice : minListPrice;
    const isSeePrices = hideTicketBisPrice || showSeePricesForCA || showSeePricesForSuperBowlEvent;

    const ticketsDisplay = isSeePrices ? (
      <div className="EventItem__Tickets">
        <div className="EventItem__PriceWrapper EventItem__SeePrice EventItem__PriceWrapper--phone">
          <FormattedMessage id="EventItem.seePrices" defaultMessage="See prices" />
        </div>
        <Button className="EventItem__PriceWrapper EventItem__SeePrice EventItem__PriceWrapper--desktop">
          <FormattedMessage id="EventItem.seePrices" defaultMessage="See prices" />
        </Button>
      </div>
    ) : showPriceInfo ? (
      <div className="EventItem__Tickets">
        <div className="EventItem__PriceWrapper EventItem__PriceWrapper--phone">
          <PriceInfo currency={ticketCurrencyCode} price={price} />
        </div>
        <Button type="secondary" className="EventItem__PriceWrapper EventItem__PriceWrapper--desktop">
          <PriceInfo currency={ticketCurrencyCode} price={price} />
        </Button>
      </div>
    ) : null;

    // Add microdata markup
    let microDataMarkup = {};
    let jsonld;
    const {microDataToggle} = checkJSONLDAndMicroDataEnabled(globalRegistry, event);
    const redirectionDomain = globalRegistry.getPropertyValue(`tb.eventpage.url.domain.${webTLD}`) || '';

    if (microDataToggle) {
      const eventJSONLD = buildEventsJSONLD([event], webTLD, 1, redirectionDomain);
      if (eventJSONLD && eventJSONLD.length && eventJSONLD[0]) {
        [jsonld] = eventJSONLD;

        microDataMarkup = {
          itemType: `${SCHEMA_ORG_PREFIX}${jsonld['@type']}`,
        };
      }
    }

    /**
     * Event Item Body Section
     *
     */
    const content = (
      <div
        className="EventItem__Body"
        ref={(node) => (this.mainNode = node)}
        onClick={(e) => this.navigateTo(e)}
        {...microDataMarkup}
        itemScope={Boolean(microDataMarkup.itemType)}
      >
        {microDataToggle && jsonld && <EventMicroData eventJSONLD={jsonld} />}
        <div className="EventItem__Info">
          <div className="EventItem__Date">{!disableDateStamp ? <DateStamp date={eventDate} /> : null}</div>
          <div className="EventItem__Details">
            <div className="EventItem__EventInfo">
              <div className="EventItem__Title" style={{wordWrap: 'break-words'}} onClick={(e) => e.stopPropagation()}>
                <CBTRedirection
                  className="EventItem__TitleLink"
                  isEnabled={isRedirectModalEnabled}
                  location={entityType}
                  countryCode={venue?.country}
                  uri={event.webURI}
                  onClick={onClick}
                  mode="optional"
                >
                  <Dotdotdot clamp={2}>{name}</Dotdotdot>
                </CBTRedirection>
              </div>
              {infoDisplay}
              <Urgency eventDateLocal={eventDateLocal} totalTickets={totalTickets} />
            </div>
            {ticketsDisplay}
          </div>
        </div>
      </div>
    );

    return <Panel className={cs(eventItemClassName || 'EventItem')} content={content} border={!hideBorder} />;
  }
}

EventItem.propTypes = {
  /**
   * Event object
   */
  event: PropTypes.object,
  threshold: PropTypes.number,
  type: PropTypes.string,

  /**
   * Options Containing the Parts of the Event Items to be Hidden
   */
  eventItemDisplayOptions: PropTypes.shape({
    disableTicketsLeft: PropTypes.bool,
    disableDateStamp: PropTypes.bool,
    disableVenueAndTime: PropTypes.bool,
    disablePrice: PropTypes.bool,
  }),

  /**
   * The Class Name used to override the Styling for the component.
   */
  className: PropTypes.string,

  /**
   * Is distance shown?
   */
  showDistance: PropTypes.bool,

  /** Is redirection modal enabled? */
  isRedirectModalEnabled: PropTypes.bool,

  /** Type of entity */
  entityType: PropTypes.string,

  /**
   * On Click Event Handler
   */
  onClick: PropTypes.func,
};

EventItem.defaultProps = {
  eventItemDisplayOptions: {
    disableTicketsLeft: false,
    disableDateStamp: false,
    disableVenueAndTime: false,
    disablePrice: false,
  },
  className: '',
};

export default EventItem;
